import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { countExpiringPricebookPeriods } from "../../services";
import { featureAccess } from "../../permissions/access";
import { useUserRole } from "./useUserRole";

/**
 * How often the count is refreshed while a screen is open. A period becomes ใกล้หมด when a
 * Bangkok day rolls over, not minute to minute, so a slow poll plus a read on tab focus is
 * enough; a write from this browser announces itself instead (below).
 */
const REFRESH_MS = 300_000;

/**
 * Period writes made in this browser. The count is shown in two places — the sidebar entry and
 * the pricebook list banner — and a write on the detail page or the overview reaches neither
 * through props, so the signal lives beside the count and every reader subscribes to it.
 */
let periodWriteCount = 0;
const periodWriteSubscribers = new Set<() => void>();

const subscribeToPeriodWrites = (onWrite: () => void) => {
  periodWriteSubscribers.add(onWrite);
  return () => {
    periodWriteSubscribers.delete(onWrite);
  };
};

const readPeriodWriteCount = () => periodWriteCount;

/** Announces that a price period was added, edited, extended or deleted, so every count re-reads. */
export function notifyPricePeriodsChanged() {
  periodWriteCount += 1;
  periodWriteSubscribers.forEach((onWrite) => onWrite());
}

/**
 * How many price periods of every book end within the warning window (ใกล้หมดช่วง): the badge
 * on the ราคาตามช่วงเวลา menu and the banner on the pricebook list. Read only for the roles that
 * may open that page; returns 0 for everyone else and never throws — a failed poll leaves the
 * previous count in place rather than blanking a warning that may still be true.
 */
export function useExpiringPricePeriodCount(): number {
  const role = useUserRole();
  const allowed = featureAccess.pricePeriodRead(role);
  const [count, setCount] = useState<number>(0);
  const writesRevision = useSyncExternalStore(
    subscribeToPeriodWrites,
    readPeriodWriteCount,
  );

  // Two writes in quick succession put two counts in flight; only the newest may land.
  const latestRequestRef = useRef<number>(0);

  const fetchCount = useCallback(async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    try {
      const {
        data: { data },
      } = await countExpiringPricebookPeriods();
      if (latestRequestRef.current !== requestId) return;
      setCount(data?.count ?? 0);
    } catch {
      // Swallowed on purpose: an ambient count on every page must not interrupt the user to
      // report that it could not be refreshed. The request suppresses the toast too.
    }
  }, []);

  useEffect(() => {
    if (!allowed) return;
    fetchCount();
    const timer = setInterval(fetchCount, REFRESH_MS);
    const handleFocus = () => fetchCount();
    window.addEventListener("focus", handleFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", handleFocus);
    };
  }, [allowed, fetchCount, writesRevision]);

  return allowed ? count : 0;
}
